/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * SocialAuthButtons Component - OAuth authentication buttons
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { Loader2 } from 'lucide-react'
import { createLogger } from '../../utils/logger'

const logger = createLogger('SocialAuthButtons')

/**
 * SocialAuthButtons Component
 *
 * Displays social authentication buttons for OAuth providers
 *
 * @param {Object} props
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.disabled - Disabled state
 */
const SocialAuthButtons = ({ loading = false, disabled = false }) => {
  const handleSocialAuth = (provider) => {
    if (loading || disabled) return

    // TODO: Implement Better Auth OAuth flow
    // For now, just log the provider
    logger.debug(`Initiating ${provider} authentication...`)

    // In production, this would redirect to the OAuth provider
    // Example: window.location.href = `/api/auth/${provider}`;
  }

  const providers = [
    {
      id: 'google',
      name: 'Google',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-900',
      borderColor: 'border-gray-300'
    },
    {
      id: 'apple',
      name: 'Apple',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ),
      bgColor: 'bg-black hover:bg-gray-900',
      textColor: 'text-white',
      borderColor: 'border-black'
    }
  ]

  return (
    <div className="space-y-3">
      {/* {providers.map((provider, index) => (
        <motion.button
          key={provider.id}
          type="button"
          onClick={() => handleSocialAuth(provider.id)}
          disabled={loading || disabled}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 * index }}
          whileHover={{ scale: disabled || loading ? 1 : 1.01 }}
          whileTap={{ scale: disabled || loading ? 1 : 0.99 }}
          className={`
                        w-full flex items-center justify-center space-x-2
                        px-4 py-2.5 rounded-lg
                        border transition-all duration-200
                        text-sm font-medium
                        ${provider.bgColor}
                        ${provider.textColor}
                        ${provider.borderColor}
                        disabled:opacity-50 disabled:cursor-not-allowed
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                    `}
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              {provider.icon}
              <span>Continue with {provider.name}</span>
            </>
          )}
        </motion.button>
      ))} */}

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">
            Or continue with email
          </span>
        </div>
      </div>
    </div>
  )
}

SocialAuthButtons.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool
}

export default SocialAuthButtons
